import z from 'zod';
import { ATTEMPT_SCAN_ORIGINAL_FILENAME_MAX_LENGTH } from '../attemptScans/constants';
import {
  AllCharLabelSchema,
  CognitoUserIdSchema,
  UnixTimestampSchema,
  UUIDSchema,
} from '../common/schemas';
import { DBTimestampSchema } from '../common/sql';
import {
  ANSWER_SHEET_ROWS,
  ATTEMPT_SUBMISSION_READ_FIELD_MAX_LENGTH,
  ATTEMPT_SUBMISSION_READ_NAME_MAX_LENGTH,
} from './constants';
import { AnswerSheetLetter, AttemptSubmissionReviewReason, AttemptSubmissionStatus } from './enums';

/**
 * DB Schemas
 */

export const DBAttemptSubmissionIdSchema = UUIDSchema;
export const DBAttemptSubmissionPageNumberSchema = z.number().int().positive();
export const DBAttemptSubmissionStatusSchema = z.enum(AttemptSubmissionStatus);
export const DBAttemptSubmissionReviewReasonsSchema = z.array(
  z.enum(AttemptSubmissionReviewReason)
);
export const DBAttemptSubmissionReadFullNameSchema = AllCharLabelSchema.min(1)
  .max(ATTEMPT_SUBMISSION_READ_NAME_MAX_LENGTH)
  .nullable();
export const DBAttemptSubmissionReadPhoneDigitsSchema = z
  .string()
  .min(1)
  .max(ATTEMPT_SUBMISSION_READ_FIELD_MAX_LENGTH)
  .nullable();
export const DBAttemptSubmissionReadSetCodeSchema = z
  .string()
  .min(1)
  .max(ATTEMPT_SUBMISSION_READ_FIELD_MAX_LENGTH)
  .nullable();
export const DBAttemptSubmissionAnswersSchema = z
  .array(z.enum(AnswerSheetLetter).nullable())
  .max(ANSWER_SHEET_ROWS);

export const DBAttemptSubmissionSchema = z.object({
  id: DBAttemptSubmissionIdSchema,
  scanId: UUIDSchema,
  firstPageNumber: DBAttemptSubmissionPageNumberSchema,
  lastPageNumber: DBAttemptSubmissionPageNumberSchema,
  pdfAssetId: UUIDSchema,
  status: DBAttemptSubmissionStatusSchema,
  reviewReasons: DBAttemptSubmissionReviewReasonsSchema,
  readFullName: DBAttemptSubmissionReadFullNameSchema,
  readPhoneDigits: DBAttemptSubmissionReadPhoneDigitsSchema,
  readSetCode: DBAttemptSubmissionReadSetCodeSchema,
  answers: DBAttemptSubmissionAnswersSchema,
  setId: UUIDSchema.nullable(),
  studentId: CognitoUserIdSchema.nullable(),
  attemptId: UUIDSchema.nullable(),
  possibleDuplicateOf: UUIDSchema.nullable(),
  submittedBy: CognitoUserIdSchema.nullable(),
  submittedAt: DBTimestampSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

/** A sheet as a list reads it, with the name and upload time of the scan it came from. */
export const DBListedAttemptSubmissionSchema = DBAttemptSubmissionSchema.extend({
  // Built from the scan's constant, since the scan's schemas import this module.
  originalFilename: AllCharLabelSchema.min(1).max(ATTEMPT_SCAN_ORIGINAL_FILENAME_MAX_LENGTH),
  uploadedAt: DBTimestampSchema,
});

/**
 * Client Schemas
 */

export const AttemptSubmissionIdSchema = DBAttemptSubmissionIdSchema;
export const AttemptSubmissionFirstPageNumberSchema = DBAttemptSubmissionPageNumberSchema.openapi({
  description: 'The page of the scan the sheet starts on, the first being 1',
  example: 3,
});
export const AttemptSubmissionLastPageNumberSchema = DBAttemptSubmissionPageNumberSchema.openapi({
  description: 'The page of the scan the sheet ends on',
  example: 4,
});
export const AttemptSubmissionStatusSchema = z.enum(AttemptSubmissionStatus).openapi({
  description: 'needs_review until the sheet is filed as an attempt, then submitted',
  example: AttemptSubmissionStatus.NeedsReview,
});
export const AttemptSubmissionReviewReasonSchema = z.enum(AttemptSubmissionReviewReason).openapi({
  description: 'Why the reader held the sheet for review',
  example: AttemptSubmissionReviewReason.StudentNotFound,
});
export const AttemptSubmissionReviewReasonsSchema = z.array(AttemptSubmissionReviewReasonSchema);
export const AttemptSubmissionReadFullNameSchema = DBAttemptSubmissionReadFullNameSchema.openapi({
  description: 'The name written on the sheet, as read',
  example: 'Isabella Nguyen',
});
export const AttemptSubmissionReadPhoneDigitsSchema =
  DBAttemptSubmissionReadPhoneDigitsSchema.openapi({
    description: 'The phone digits written on the sheet, as read',
    example: '4821',
  });
export const AttemptSubmissionReadSetCodeSchema = DBAttemptSubmissionReadSetCodeSchema.openapi({
  description: 'The quiz code written on the sheet, as read',
  example: 'KX7M2A',
});
export const AttemptSubmissionAnswerSchema = z.enum(AnswerSheetLetter).nullable().openapi({
  description: 'The letter shaded on a row, or null where the row counts as unanswered',
  example: AnswerSheetLetter.B,
});
export const AttemptSubmissionPossibleDuplicateOfSchema = UUIDSchema.nullable().openapi({
  description:
    "The student's earlier attempt at the set with the same answers, for which the sheet is held",
  example: null,
});
export const AttemptSubmissionAnswersSchema = z
  .array(AttemptSubmissionAnswerSchema)
  .max(ANSWER_SHEET_ROWS)
  .openapi({ description: 'The answer on each row, row 1 first' });

export const AttemptSubmissionSchema = z.object({
  id: AttemptSubmissionIdSchema,
  scanId: UUIDSchema,
  firstPageNumber: AttemptSubmissionFirstPageNumberSchema,
  lastPageNumber: AttemptSubmissionLastPageNumberSchema,
  pdfAssetId: UUIDSchema,
  status: AttemptSubmissionStatusSchema,
  reviewReasons: AttemptSubmissionReviewReasonsSchema,
  readFullName: AttemptSubmissionReadFullNameSchema,
  readPhoneDigits: AttemptSubmissionReadPhoneDigitsSchema,
  readSetCode: AttemptSubmissionReadSetCodeSchema,
  answers: AttemptSubmissionAnswersSchema,
  setId: UUIDSchema.nullable(),
  studentId: CognitoUserIdSchema.nullable(),
  attemptId: UUIDSchema.nullable(),
  possibleDuplicateOf: AttemptSubmissionPossibleDuplicateOfSchema,
  submittedBy: CognitoUserIdSchema.nullable(),
  submittedAt: UnixTimestampSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const ListedAttemptSubmissionSchema = AttemptSubmissionSchema.extend({
  originalFilename: DBListedAttemptSubmissionSchema.shape.originalFilename.openapi({
    description: 'The name the scan the sheet was read from was uploaded under',
    example: 'Year 11 physics quiz.pdf',
  }),
  uploadedAt: UnixTimestampSchema.openapi({
    description: 'When the scan the sheet was read from was uploaded',
    example: 1759036200000,
  }),
});
